import PartySocket from "partysocket";

import { getErrorMessage } from "@fitness-app/utils";
import { parseIncomingClientRealtimeMessage } from "@fitness-app/utils/src/chat/realtimeParsers";
import { RealtimeRoom } from "@fitness-app/utils/src/notifications/realtimeRooms";

import { getLoggedUser } from "../../../helpers/getLoggedUser";
import { type AppThunk } from "../../../index";
import {
  addMessageToChannel,
  removeMessageFromChannel,
  subscribeToChannelFailed,
  subscribeToChannelSuccess,
  updateMessageInChannel,
  updateMessagesToMark,
} from "../reducer";

interface SubscribeOptions {
  /**
   * Called when the socket comes back after a drop. The room does not replay what was sent
   * while the client was offline, so the caller pulls the latest page itself.
   */
  onReconnect?: () => void;
}

export const subscribeToChannelMessage =
  (channelId: string, { onReconnect }: SubscribeOptions = {}): AppThunk =>
  async (dispatch, getState, { auth, parties, config }) => {
    try {
      const { id } = await getLoggedUser(auth);

      // Always a fresh socket. The previous version close()d the old PartySocket and then kept
      // attaching listeners to it - but close() switches the library's auto-reconnect off for
      // good, so the first dropped connection (laptop lid, tab in the background) left the chat
      // silently without realtime until a hard reload. Drop the registry entry before closing
      // so nothing treats the retired socket as current.
      const previous = parties.chatRooms[channelId];
      if (previous) {
        parties.chatRooms[channelId] = null;
        previous.close();
      }

      const socket = new PartySocket({
        host: config.realtimeServerUrl || "localhost:1999",
        room: channelId,
        party: RealtimeRoom.ChatRoom,
        // Evaluated on every (re)connect, so a socket that comes back after the machine slept
        // presents the refreshed JWT rather than the one captured when the thread was opened.
        query: async () => ({ token: (await auth.getSession())?.data?.session?.access_token }),
      });
      parties.chatRooms[channelId] = socket;

      let connectedBefore = false;

      socket.addEventListener("message", (event: WebSocketEventMap["message"]) => {
        const parsedEvent = parseIncomingClientRealtimeMessage(event.data);

        if (parsedEvent.type === "initial") {
          dispatch(subscribeToChannelSuccess({ channelId }));
          if (connectedBefore) {
            onReconnect?.();
          }
          connectedBefore = true;
        }
        if (parsedEvent.type === "message") {
          if (parsedEvent.operation === "add") {
            dispatch(addMessageToChannel({ message: parsedEvent.data, channelId }));
            const { activeChannels } = getState().chat;
            const foundChannel = activeChannels.find((channel) => channel.id === channelId);
            if (foundChannel && !foundChannel.minimized) {
              dispatch(
                updateMessagesToMark({
                  messages: [parsedEvent.data].filter((item) => item.authorId !== id).map((item) => item.id),
                  channelId,
                }),
              );
            }
          }

          if (parsedEvent.operation === "delete" || (parsedEvent.operation === "update" && parsedEvent.data.deleted)) {
            dispatch(removeMessageFromChannel({ messageId: parsedEvent.data.id, channelId }));
          }

          if (parsedEvent.operation === "update" && !parsedEvent.data.deleted) {
            dispatch(updateMessageInChannel({ message: parsedEvent.data, channelId }));
          }
        }
      });

      // No "close" handler: a drop is retried by PartySocket itself with backoff, and a close we
      // asked for (unsubscribe, or the swap above) must stay closed.

      socket.addEventListener("error", (error) => {
        dispatch(subscribeToChannelFailed(getErrorMessage(error)));
      });
    } catch (error) {
      dispatch(subscribeToChannelFailed(getErrorMessage(error)));
    }
  };
